/**
 * CookieConsent
 *
 * GDPR strategy:
 *   • Authenticated users: consent is captured implicitly when they accept the
 *     Terms of Service during sign-up. We mark their localStorage automatically
 *     (see `markCookieConsentAccepted`) so they never see this banner.
 *   • Unauthenticated visitors: a one-time notice banner appears. Once
 *     acknowledged (accept or dismiss) it is stored in localStorage and never
 *     shown again on the same device/browser.
 *
 * We only use strictly-necessary cookies (auth session) and first-party
 * analytics, both of which are covered by the ToS/Privacy Policy.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "intellr_cookie_consent_v2";

/** Returns the stored consent record, or null if not yet decided. */
export function getCookieConsent(): { accepted: boolean; timestamp: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Called from Auth flow (sign-up + Google sign-in) so that authenticated users
 * are permanently exempt from the banner.
 */
export function markCookieConsentAccepted(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ accepted: true, timestamp: new Date().toISOString(), via: "signup" })
    );
  } catch {
    // localStorage unavailable (e.g. Firefox strict mode) — safe to ignore
  }
}

export default function CookieConsent() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Authenticated users already accepted ToS — no banner needed
    if (user) {
      // Lazily persist so future page loads never show the banner even after logout
      if (!getCookieConsent()) markCookieConsentAccepted();
      return;
    }
    // Show banner only if the visitor hasn't decided yet
    if (!getCookieConsent()) setVisible(true);
  }, [user]);

  const dismiss = (accepted: boolean) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted, timestamp: new Date().toISOString(), via: "banner" })
      );
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      aria-live="polite"
      className="fixed bottom-0 inset-x-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg"
    >
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="text-[13px] text-slate-500 flex-1 leading-relaxed">
          We use essential cookies for authentication and first-party analytics.
          By continuing to browse you agree to our{" "}
          <Link to="/privacy" className="text-teal-600 hover:underline font-medium">
            Privacy Policy
          </Link>
          .{" "}
          <Link to="/auth" className="text-teal-600 hover:underline font-medium">
            Create an account
          </Link>{" "}
          to manage your preferences.
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => dismiss(false)}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-[13px] font-medium hover:bg-slate-50 transition-colors"
          >
            Dismiss
          </button>
          <button
            onClick={() => dismiss(true)}
            className="px-4 py-2 rounded-lg bg-teal-500 text-white text-[13px] font-semibold hover:bg-teal-600 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
